import { isEmpty } from 'radash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { useValidationsStore, ValidationStatus } from '@/components/datamodel/hooks/useValidationsStore';
import { BaseValidation } from '@/components/datamodel/types';
import { useDataModel } from '@/hooks/useDataModel';
import { useItemChangesQueue } from '@/hooks/useItemChangesQueue';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModelAttributeTypeName, Item } from '@/types/DataModel';

import { sanitizeItemForSave, useRemoteValidation } from '../../ItemEditor.queries';
import { handleValidationError,handleValidationSuccess } from './validationResponseHandlers';
import { useValidationWorker } from './useValidationWorker';
import { type PreparedValidationOperations } from './validation.shared';

interface UseValidationsProps {
    isReadyForInitialSetup?: boolean,
    isNewItem?: boolean,
    initialItem?: Item,
    initialValidations?: Record<string, BaseValidation[]>,
}

const IMMEDIATE_VALIDATION_TYPES = new Set<DataModelAttributeTypeName>([
    DataModelAttributeTypeName.Boolean,
    DataModelAttributeTypeName.Enum,
    DataModelAttributeTypeName.EnumSet,
    DataModelAttributeTypeName.OpenEnum,
    DataModelAttributeTypeName.OpenEnumSet,
    DataModelAttributeTypeName.Codelist,
    DataModelAttributeTypeName.CodelistSet,
    DataModelAttributeTypeName.OpenCodelist,
    DataModelAttributeTypeName.OpenCodelistSet,
]);

const EMPTY_ROWS_TYPES = new Set<DataModelAttributeTypeName>([
    DataModelAttributeTypeName.Collection,
    DataModelAttributeTypeName.MultiReference,
]);

const useValidations = ({ isReadyForInitialSetup, isNewItem, initialItem, initialValidations }: UseValidationsProps) => {
    const { watch, setValue, getValues } = useFormContext();
    const [initialSetupDone, setInitialSetupDone] = useState(false);
    const [firstValidationSkipped, setFirstValidationSkipped] = useState(false); //  Concerns only existing item
    const calculatedAttributesRef = useRef<Set<string>>(new Set());
    const { isDirty } = useFormState();
    const { dataModel } = useDataModel();
    const { toast } = useToast();
    const { t } = useTranslation();
    const workerRef = useValidationWorker();

    // Helper function to update validation status based on validationErrors and validationWarnings
    const updateValidationStatusFromState = (
        errors: typeof validationErrors,
    ) => {
        // Check if validationErrors has any entries (not null and not empty)
        const hasErrors = errors && Object.keys(errors).length > 0;

        if (hasErrors) {
            setValidationStatus(ValidationStatus.IS_NOT_COMPLIANT);
        } else {
            // If validationErrors is null or empty, set to IS_COMPLIANT regardless of warnings
            setValidationStatus(ValidationStatus.IS_COMPLIANT);
        }
    };

    const {
        clearErrors,
        clearWarnings,
        setValidationStatus,
        clearLocalValidationErrors,
        applyServerValidations,
        validationErrors,
        validationWarnings,
    } = useValidationsStore(
        useShallow((state) => ({
            clearErrors: state.clearErrors,
            clearWarnings: state.clearWarnings,
            setValidationStatus: state.setStatus,
            clearLocalValidationErrors: state.clearLocalValidationErrors,
            applyServerValidations: state.applyServerValidations,
            validationErrors: state.validationErrors,
            validationWarnings: state.validationWarnings,
        })),
    );

    const { initializeWithStates, applyAttributeStates } =
        useAttributesStateStore(
            useShallow((state) => ({
                initializeWithStates: state.initializeWithStates,
                applyAttributeStates: state.applyAttributeStates,
            })),
        );

    // Pre-built map of attribute name → type, derived from dataModel once loaded.
    // Used by buildChangesPatch to avoid per-change dataModel lookups.
    const attributeTypeByName = useMemo((): Record<string, DataModelAttributeTypeName | undefined> => {
        if (!dataModel) return {};
        return Object.fromEntries(
            dataModel.allAttributes().map((attr) => [attr.name, attr.typeName]),
        );
    }, [dataModel]);

    // a Ref because it is defined later/below
    const updateItemRef = useRef<null | ((attributeName: string, value: any) => void)>(null);

    const { mutateAsync: doValidationRequest } = useRemoteValidation({
        onSuccess: (validationResponse, variables) => {
            logger.debug(
                'useValidations: Received a validation response',
                validationResponse,
            );
            void handleValidationSuccess(
                validationResponse,
                variables,
                updateItemRef.current,
                getValues,
                calculatedAttributesRef,
                applyAttributeStates,
                setValidationsOnAttributes,
            );
        },
        onError: (error, variables) => {
            handleValidationError(
                error,
                variables,
                setValidationStatus,
                toast,
                t,
            );
            logger.error('Got error from validation: ' + error);
        },
    });

    useEffect(() => {
        const $angularJSInjector = getInjector();
        const $rootScope = getRootScope($angularJSInjector);
        $rootScope.isDirty = isDirty;
    }, [isDirty]);

    // Validation queue
    const {
        start: startValidationQueue,
        stop: stopValidationQueue,
        enqueueChange,
        updateProcessed: updateLastValidatedItem,
        isProcessing: isValidationInProgress,
        isStarted: isValidationQueueStarted,
        flush: flushValidationQueue,
    } = useItemChangesQueue<Item>({
        defaultDelayMs: 3000,
        processor: async ({ patch, processingItem, processedItem }) => {

            // For an existing item which is not "Dirty", we skip the first validation because this request
            // is fired by us setting-up the item (e.g. updating multi-reference attributes)
            // Note: We "hope" that the setup is done at this point, but this is not guaranteed
            if (!isNewItem && !firstValidationSkipped) {
                setFirstValidationSkipped(true);
                if (!isDirty) {
                    logger.debug('ValidationQueue: Skipping validation for existing item that is not dirty');
                    return;
                }
            }

            setValidationStatus(ValidationStatus.IS_RUNNING);
            const attributeNames = Object.keys(patch);
            const oldItem = sanitizeItemForSave(processedItem);  // last validated snapshot
            const newItem = sanitizeItemForSave(processingItem); // "in-flight" item / processedItem + patch applied

            const finalAttributeNames = (!oldItem || attributeNames.includes('category__')) ?
                null : attributeNames;

            logger.debug(
                'ValidationQueue: Doing validation the request',
                structuredClone({
                    attributeNames: finalAttributeNames,
                    oldItem: oldItem,
                    newItem: newItem,
                }),
            );

            await doValidationRequest({
                attributeNames: finalAttributeNames,
                newItem,
                oldItem,
                attributeStatesOnly: false,
            });
        },
    });

    // Used to update the current item with calculated attributes
    const updateItem = useCallback((attributeName: string, value: any) => {
        // Order is important, we want to "sneak in" the calculated value to the last validated item first
        // before we update the form
        updateLastValidatedItem(attributeName, value);
        // logger.debug('ValidationQueue: updateItem() updated last validated item', { [attributeName]: value });
        setValue(attributeName, value);
    }, [setValue, updateLastValidatedItem]);

    useEffect(() => {
        updateItemRef.current = updateItem;
    }, [updateItem]);

    // Queue setup
    useEffect(() => {
        if (!initialItem) return;
        if (isNewItem) {
            startValidationQueue({} as Item);
        } else {
            startValidationQueue(initialItem);
        }
        return () => {
            stopValidationQueue();
        };
    }, [initialItem, isNewItem, startValidationQueue, stopValidationQueue]);

    // Handle initial setup
    useEffect(() => {
        if (initialSetupDone || !isReadyForInitialSetup || !initialItem || !isValidationQueueStarted || !dataModel) {
            return;
        }
        if (isNewItem) {
            // We validate the whole new item.
            // Note: This would force "full" validation because "category__" is set at this point
            const formValues = getValues();
            const allFields = Object.keys(formValues);
            const { changesPatch } = buildChangesPatch(allFields, formValues);
            enqueueChange(changesPatch);
            logger.debug(
                'ValidationQueue: Doing initial validation for new item with changes',
                changesPatch,
            );
            flushValidationQueue();
            setFirstValidationSkipped(true); // No need to skip for new item
        } else if (
            // Is an existing item, just apply it's attributesStates
            Object.keys(initialItem || {}).length > 1 &&
            !!initialItem?.category__
        ) {
            logger.debug('ValidationQueue: Applying initial attributeStates', structuredClone(initialItem?.attributeStates__));
            applySavedAttributeStates(initialItem?.attributeStates__ || []);
        }
        setInitialSetupDone(true);

    }, [initialItem, isNewItem, initialSetupDone, firstValidationSkipped, isReadyForInitialSetup, isValidationQueueStarted, dataModel]);

    // Handle validation on form field changes
    useEffect(() => {
        if (!initialSetupDone || !dataModel) return;

        const itemChangesSubscription = watch(
            (formValues, { name: fieldPath }) => {
                if (!fieldPath || !dataModel) return;

                const { changesPatch, hasImmediateAttribute } = buildChangesPatch(fieldPath, formValues as Record<string, any>);

                if (enqueueChange(changesPatch)) {
                    logger.debug('ValidationQueue: Enqueuing attribute change', changesPatch);
                }
                if (hasImmediateAttribute) {
                    flushValidationQueue();
                }
            },
        );

        return () => {
            itemChangesSubscription.unsubscribe();
        };
    }, [watch, dataModel, enqueueChange, initialSetupDone]);

    const buildChangesPatch = useCallback((attributeNames: string | string[], formValues: Record<string, any>) => {
        const changesPatch: Record<string, any> = {};

        const names = Array.isArray(attributeNames)
            ? attributeNames
            : [attributeNames];

        let hasImmediateAttribute = false;

        names.forEach((attrName) => {
            // Convert nested paths to top-level attribute
            // e.g. "retailer_targets.0.simple_date_time" → "retailer_targets"
            const attributeName = attrName.includes('.')
                ? attrName.split('.')[0]
                : attrName;

            const attributeType = attributeTypeByName[attributeName];

            if (attributeType && IMMEDIATE_VALIDATION_TYPES.has(attributeType)) {
                hasImmediateAttribute = true;
            }

            const value = formValues[attributeName];
            if (filterRedundantChanges(value, attributeType)) {
                changesPatch[attributeName] = value === undefined ? null : value;
            }
        });

        return { changesPatch, hasImmediateAttribute };
    }, [attributeTypeByName]);

    // Filter out redundant changes which may cause unnecessary validation
    // FIXME: This is a temporary fix. We should remove this after LAX-9559 is resolved
    const filterRedundantChanges = useCallback((value: any, attributeType: DataModelAttributeTypeName | undefined) => {
        if (!attributeType) return true;

        // Do not validate empty rows
        if (EMPTY_ROWS_TYPES.has(attributeType) && Array.isArray(value)) {
            return value.some((v) => !isEmpty(v));
        }

        return true;
    }, []);

    useEffect(() => {
        // This hook runs when we first load the item to edit it. It's responsible for
        // initializing the attributes errors and warnings, in addition to the validation status.
        if (initialValidations) {
            logger.debug(
                'useValidations: Setting initial validations',
                initialValidations,
            );
            setValidationsOnAttributes(initialValidations).catch((err) => {
                logger.error('useValidations: Failed to set initial validations', err);
            });
        }

        return () => {
            clearErrors();
            clearWarnings();
            clearLocalValidationErrors();
        };
    }, [initialValidations]);

    // Watch validationErrors and validationWarnings to update validation status
    // This ensures status is always in sync with the actual validation state
    useEffect(() => {
        // Don't update status if validations are still running
        if (isValidationInProgress) {
            return;
        }

        // Wait until validationErrors and validationWarnings are initialized (not undefined)
        // In Zustand, they're always defined (initialized as {}), so we check if they exist
        if (
            validationErrors !== undefined &&
            validationWarnings !== undefined
        ) {
            updateValidationStatusFromState(validationErrors);
        }
    }, [validationErrors, validationWarnings, isValidationInProgress]);

    const applySavedAttributeStates = (attributeStates: string[]) => {
        // Use the new atomic initialization method
        initializeWithStates(attributeStates);
    };

    const setValidationsOnAttributes = useCallback(async (
        validations: Record<string, BaseValidation[]>,
        validatedAttributeNames?: string[],
        _initialStatus?: ValidationStatus,
    ) => {
        logger.debug(
            'useValidations: Setting validations on attributes',
            validations,
            'validated attribute names:',
            validatedAttributeNames,
        );

        const worker = workerRef.current;
        if (!worker) return;

        const ops: PreparedValidationOperations = await worker.prepareValidationOperations({ validations, validatedAttributeNames });

        // Single set() for the whole response so mounted fields are not
        // equality-checked once per attribute (LAX-9602).
        applyServerValidations(ops);
    }, [applyServerValidations]);
};

export { useValidations };
